import { useEffect, useState } from "react";

import { useAuth } from "../auth/auth-context";
import { listBranches } from "./branches";
import { useActiveBranch } from "./use-active-branch";
import { useBookingLocation } from "../calendar/booking-location";

/**
 * **Each location prices in its own currency** (QA4-D6), so a figure at a
 * location is formatted in that location's — never in `user.currency`, which
 * is the clinic's home currency and prices only what every location shares:
 * salaries, shared expenses and supplier lists.
 *
 * The clinic's locations are read once and shared by every screen that asks;
 * a currency change in Settings calls `refreshBranchCurrencies` so nothing
 * goes on formatting at the old exponent.
 */
let cache: Promise<Map<string, string>> | null = null;
const listeners = new Set<() => void>();

function load(): Promise<Map<string, string>> {
  cache ??= listBranches()
    .then((branches) => new Map(branches.map((b) => [b.id, b.currency])))
    .catch(() => {
      cache = null;
      return new Map<string, string>();
    });
  return cache;
}

/** Forget what was read, and re-read it — after a location's currency changed. */
export function refreshBranchCurrencies(): void {
  cache = null;
  void load().then(() => {
    for (const listener of listeners) listener();
  });
}

/**
 * The currency of a location — the active one when none is named. Falls back
 * to the home currency while the list loads, which for every clinic whose
 * locations share one currency is already the right answer.
 */
export function useBranchCurrency(branchId?: string | null): string {
  const active = useActiveBranch();
  const home = useAuth().user?.currency ?? "USD";
  // A booking's panel on the combined calendar prices at the booking's location.
  const booking = useBookingLocation();
  const id = branchId ?? booking?.branchId ?? active;
  const [currencies, setCurrencies] = useState<Map<string, string> | null>(null);

  useEffect(() => {
    let live = true;
    const read = (): void => {
      void load().then((map) => {
        if (live) setCurrencies(map);
      });
    };
    read();
    listeners.add(read);
    return () => {
      live = false;
      listeners.delete(read);
    };
  }, []);

  return (id === null ? undefined : currencies?.get(id)) ?? home;
}
